import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Select } from '@nazka/nazka.mapframe.components';
import { faQuestionCircle, faWifiSlash, faSearch } from '@fortawesome/pro-solid-svg-icons';
import {
  NavLink, 
  useLocation, 
  useMatch, 
  useNavigate,
} from 'react-router-dom';

import UploadNotifications from '@App/components/UploadNotifications/UploadNotifications';
import { useNetworkState } from '@services/hooks.service';
import { isMobileNavbar } from '@services/utils.service';
import { useTranslation } from '@utils/i18n/i18n.consumers';
import {
  setTourGuideState,
  setTypeOfTour,
  setTourStepIndex,
  setSearchBarExpanded, 
} from '@library/GuidedTour/guided-tour.slice';
import { trackEvent } from '@services/analytics.service';

import { GUIDE_OPTIONS } from './navbar.constants';

import GlossarySearch from './GlossarySearch/GlossarySearch';
import NavbarMobileMenu from './NavbarMobileMenu/NavbarMobileMenu';

import './navbar.styl';

const LANGUAGE_OPTIONS = [
  { id: 'en', label: 'EN' },
  { id: 'ch', label: 'CH' },
];

function getGuideOptionByPath(pathname) {
  if (pathname.startsWith('/learn')) {
    return GUIDE_OPTIONS[0];
  }

  if (pathname.startsWith('/risks')) {
    return GUIDE_OPTIONS[1];
  }

  if (pathname.startsWith('/adaptation')) {
    return GUIDE_OPTIONS[2];
  }

  return GUIDE_OPTIONS[0];
}

function Navbar({ landing, explore }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate()
  const isOnline = useNetworkState()
  const searchBarExpanded = useSelector(state => state.guidedTour.searchBarExpanded)
  const [isGuideDropdownOpen, setIsGuideDropdownOpen] = useState(false)
  const guideDropdownRef = useRef(null);

  const { breakpoint } = useSelector(state => state.windowObserver);

  const { t, setLang, lang } = useTranslation();

  const showUploadNotifications = useMatch('/adaptation/*');
  const selectedGuide = getGuideOptionByPath(location.pathname);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(event.target)) {
        setIsGuideDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleGuidedTooltipClick = () => {
    if (location.pathname === '/explore') {
      setIsGuideDropdownOpen(prevState => !prevState);
    } else {
      const guideOption = getGuideOptionByPath(location.pathname);
      navigate(guideOption.path);
      dispatch(setTypeOfTour(guideOption.id));
      dispatch(setTourStepIndex(0));
      dispatch(setTourGuideState(true));
    }
  }

  return (
    <div className={`navbar ${landing ? 'navbar--landing' : ''}`}>
      <NavLink to="/" className="navbar__logo">
        <img src="/img/logo/climate-champions-logo.svg" alt="logo" />
      </NavLink>
      <div className="navbar__cta-wrapper">
        <Icon
          icon={faWifiSlash}
          css={{
            display: isOnline ? 'none' : 'block',
            color: 'red',
          }}
          size="25px"
        />
        {showUploadNotifications && <UploadNotifications />}
        {import.meta.env.VITE_NAZKA_ENV === 'development' && (
          <Select
            options={LANGUAGE_OPTIONS}
            css={{
              borderRadius: '8px',
              border: 'none',
              width: '80px',
            }}
            value={lang}
            onInputChange={(e) => {
              setLang(e.id);
              trackEvent(`language_changed_to_${e.id}`);
            }}
            className={`navbar__language-select my-fourth-learn-step ${searchBarExpanded ? 'navbar__language-select--search-expanded' : ''}`}
          />
        )}
        {isMobileNavbar(breakpoint) && !landing ? (
          <NavbarMobileMenu explore={explore} />
        ) : (
          <>
            {!landing && (
              <>
                <Button
                  className="glossary-search__button"
                  layout="outlined"
                  color="#126BC2"
                  icon={faSearch}
                  onClick={() => dispatch(setSearchBarExpanded(true))}
                  hover={false}
                  css={{
                    borderRadius: '8px',
                  }}
                >
                  {t('Search')}
                </Button>
                <div
                  className={`navbar__guide-dropdown ${searchBarExpanded ? 'navbar__help-btn--search-expanded' : ''}`}
                  ref={guideDropdownRef}
                >
                  <Button
                    icon={faQuestionCircle}
                    css={{
                      borderRadius: '8px',
                      flexDirection: 'row-reverse',
                    }}
                    color="#126BC2"
                    className="navbar__help-btn"
                    onClick={handleGuidedTooltipClick}
                  >
                    {t('Help')}
                  </Button>
                  {isGuideDropdownOpen && (
                    <div className="navbar__guide-dropdown__menu">
                      {GUIDE_OPTIONS.map(option => (
                        <button
                          key={option.id}
                          type="button"
                          className={`navbar__guide-dropdown__item ${selectedGuide.id === option.id ? 'navbar__guide-dropdown__item--active' : ''}`}
                          onClick={() => {
                            navigate(option.path)
                            dispatch(setTypeOfTour(option.id))
                            dispatch(setTourStepIndex(0))
                            dispatch(setTourGuideState(true))
                          }}
                        >
                          {t(option.label)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            {!explore && (
              <Button
                className={`navbar__explore-btn my-sixth-learn-step ${searchBarExpanded ? 'navbar__explore-btn--search-expanded' : ''}`}
                themeColor="secondary"
                onClick={() => navigate('/explore')}
              >
                {t('Explore page')}
              </Button>
            )}
          </>
        )}
      </div>
      {searchBarExpanded && <GlossarySearch onClose={() => dispatch(setSearchBarExpanded(false))} />}
    </div>
  );
}

export default Navbar
